import {
  computeRingCentroid,
  parseBoundaryGeoJsonRing,
  pointInPolygon,
} from "./mapMeasurements";

const GAUGE_LABELS = ["Noroeste", "Nordeste", "Sudeste", "Sudoeste", "Centro"];

const DEFAULT_DEMO_MM = [8, 22, 48, 16, 32];

export function normalizeFarmName(name) {
  return String(name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function isRafaelFarmName(name) {
  const normalized = normalizeFarmName(name);
  return (
    normalized.includes("sao rafael") || normalized.includes("san rafael")
  );
}

export function findSanRafaelSubFarm(groupFarms) {
  const farms = groupFarms || [];
  const named = farms.find((farm) => isRafaelFarmName(farm && farm.nome_da_fazenda));
  if (named) return named;
  return farms.find((farm) => farm && farm.parent && isRafaelFarmName(farm.nome_da_fazenda));
}

export function distanceMeters(a, b) {
  if (!a || !b) return 0;
  const earthRadius = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(h));
}

function clampPointInsideRing(point, ring, centroid) {
  if (pointInPolygon(point, ring)) {
    return point;
  }
  if (!centroid) return point;
  return {
    lat: point.lat * 0.72 + centroid.lat * 0.28,
    lng: point.lng * 0.72 + centroid.lng * 0.28,
  };
}

export function buildGaugePositionsFromRing(ring) {
  if (!Array.isArray(ring) || ring.length < 3) {
    return [];
  }

  const centroid = computeRingCentroid(ring);
  const lats = ring.map((point) => point.lat);
  const lngs = ring.map((point) => point.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const insetLat = (maxLat - minLat) * 0.12;
  const insetLng = (maxLng - minLng) * 0.12;

  const rawPoints = [
    { lat: maxLat - insetLat, lng: minLng + insetLng },
    { lat: maxLat - insetLat, lng: maxLng - insetLng },
    { lat: minLat + insetLat, lng: maxLng - insetLng },
    { lat: minLat + insetLat, lng: minLng + insetLng },
    centroid,
  ].filter(Boolean);

  return rawPoints.map((point, index) => {
    const position = clampPointInsideRing(point, ring, centroid);
    return {
      id: `gauge-${index + 1}`,
      label: GAUGE_LABELS[index] || `Ponto ${index + 1}`,
      lat: position.lat,
      lng: position.lng,
      mm: DEFAULT_DEMO_MM[index] || 0,
    };
  });
}

const IDW_POWER = 2;
const IDW_RADIUS_METERS = 12000;
const IDW_EXACT_METERS = 8;

function projectRainPoint(lat, lng, origin) {
  const midLat = (Number(origin.lat) * Math.PI) / 180;
  return {
    x: (Number(lng) - Number(origin.lng)) * 111320 * Math.cos(midLat),
    y: (Number(lat) - Number(origin.lat)) * 111320,
  };
}

function rainNodesFromGauges(gauges) {
  const valid = (gauges || []).filter((gauge) => {
    const lat = Number(gauge && gauge.lat);
    const lng = Number(gauge && gauge.lng);
    return Number.isFinite(lat) && Number.isFinite(lng);
  });
  if (!valid.length) return { origin: null, nodes: [] };

  const origin = {
    lat: valid.reduce((sum, gauge) => sum + Number(gauge.lat), 0) / valid.length,
    lng: valid.reduce((sum, gauge) => sum + Number(gauge.lng), 0) / valid.length,
  };
  const merged = new Map();
  valid.forEach((gauge) => {
    const lat = Number(gauge.lat);
    const lng = Number(gauge.lng);
    const key = `${lat.toFixed(6)}:${lng.toFixed(6)}`;
    const xy = projectRainPoint(lat, lng, origin);
    const current = merged.get(key);
    if (current) {
      current.mm = (current.mm + (Number(gauge.mm) || 0)) / 2;
      return;
    }
    merged.set(key, {
      lat,
      lng,
      mm: Number(gauge.mm) || 0,
      x: xy.x,
      y: xy.y,
    });
  });
  return { origin, nodes: Array.from(merged.values()) };
}

function interpolateIdw(xy, nodes, power, exactMeters, radiusMeters) {
  let nearest = nodes[0];
  let nearestDist = Infinity;
  let weightSum = 0;
  let valueSum = 0;

  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const dist = Math.hypot(xy.x - node.x, xy.y - node.y);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = node;
    }
    if (dist <= exactMeters) return node.mm;
    if (dist > radiusMeters) continue;
    const weight = 1 / dist ** power;
    weightSum += weight;
    valueSum += weight * node.mm;
  }

  if (weightSum <= 0) return nearest ? nearest.mm : 0;
  return Math.max(0, valueSum / weightSum);
}

export function createRainFieldInterpolator(gauges, options = {}) {
  const power = Number(options.power) > 0 ? Number(options.power) : IDW_POWER;
  const exactMeters =
    Number(options.minDistanceMeters) > 0
      ? Number(options.minDistanceMeters)
      : IDW_EXACT_METERS;
  const radiusMeters =
    Number(options.radiusMeters) > 0
      ? Number(options.radiusMeters)
      : IDW_RADIUS_METERS;
  const { origin, nodes } = rainNodesFromGauges(gauges);
  if (!origin || !nodes.length) return () => 0;
  if (nodes.length === 1) return () => nodes[0].mm;

  return (point) => {
    if (!point) return 0;
    const xy = projectRainPoint(point.lat, point.lng, origin);
    return interpolateIdw(xy, nodes, power, exactMeters, radiusMeters);
  };
}

export function interpolateRainMmAtPoint(
  point,
  gauges,
  power = IDW_POWER,
  minDistanceMeters = IDW_EXACT_METERS
) {
  if (!point || !Array.isArray(gauges) || gauges.length === 0) {
    return 0;
  }
  return createRainFieldInterpolator(gauges, { power, minDistanceMeters })(point);
}

export const RAIN_HEATMAP_GRID_STEPS = 26;

export function heatmapCellRadiusMeters(ring, gridSteps = RAIN_HEATMAP_GRID_STEPS) {
  if (!Array.isArray(ring) || ring.length < 3) return 140;
  const lats = ring.map((point) => Number(point.lat));
  const lngs = ring.map((point) => Number(point.lng));
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const midLat = (minLat + maxLat) / 2;
  const latMeters = ((maxLat - minLat) / gridSteps) * 111320;
  const lngMeters =
    ((maxLng - minLng) / gridSteps) *
    111320 *
    Math.cos((midLat * Math.PI) / 180);
  const cell = Math.max(latMeters, lngMeters, 50);
  return cell * 0.72;
}

export function buildHeatmapWeightedPoints(ring, gauges, gridSteps = RAIN_HEATMAP_GRID_STEPS) {
  const effectiveRing = resolveHeatmapRing(ring, gauges);
  if (!Array.isArray(effectiveRing) || effectiveRing.length < 3 || !Array.isArray(gauges)) {
    return [];
  }

  const lats = effectiveRing.map((point) => point.lat);
  const lngs = effectiveRing.map((point) => point.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latStep = (maxLat - minLat) / gridSteps;
  const lngStep = (maxLng - minLng) / gridSteps;
  const points = [];
  const seen = new Set();

  function pushPoint(point, mm, boost = 1) {
    const weight = Math.max(Number(mm) || 0, 0);
    if (weight <= 0) return;
    const key = `${point.lat.toFixed(6)}:${point.lng.toFixed(6)}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push({
      lat: point.lat,
      lng: point.lng,
      mm: weight,
      weight: weight * boost,
    });
  }

  (gauges || []).forEach((gauge) => {
    pushPoint(
      { lat: gauge.lat, lng: gauge.lng },
      gauge.mm,
      2.5
    );
  });

  for (let row = 0; row <= gridSteps; row += 1) {
    for (let col = 0; col <= gridSteps; col += 1) {
      const sample = {
        lat: minLat + latStep * row,
        lng: minLng + lngStep * col,
      };
      if (!pointInPolygon(sample, effectiveRing)) {
        continue;
      }
      const mm = interpolateRainMmAtPoint(sample, gauges);
      pushPoint(sample, mm, 1);
    }
  }

  return points;
}

export function getHeatmapMaxIntensity(points, gauges) {
  const values = [
    ...(points || []).map(
      (point) => Number(point.weight != null ? point.weight : point.mm) || 0
    ),
    ...(gauges || []).map((gauge) => (Number(gauge.mm) || 0) * 2.5),
  ].filter((value) => value > 0);

  if (!values.length) return 1;
  return Math.max(...values, 1);
}

export function getHeatmapGradient() {
  return [
    "rgba(33, 150, 243, 0)",
    "rgba(33, 150, 243, 0.45)",
    "rgba(0, 188, 212, 0.6)",
    "rgba(76, 175, 80, 0.75)",
    "rgba(255, 235, 59, 0.88)",
    "rgba(255, 152, 0, 0.95)",
    "rgba(244, 67, 54, 1)",
  ];
}

export function formatRainMm(value) {
  const mm = Number(value);
  if (!Number.isFinite(mm)) return "0";
  return String(Math.round(mm));
}

export function sumGaugeRainMm(gauges) {
  return (gauges || []).reduce(
    (total, gauge) => total + (Number(gauge.mm) || 0),
    0
  );
}

export function averageGaugeRainMm(gauges) {
  const list = gauges || [];
  if (!list.length) return 0;
  return sumGaugeRainMm(list) / list.length;
}

export function buildGaugesFromStations(stations) {
  return (stations || []).map((station) => ({
    id: station.stationUuid,
    stationUuid: station.stationUuid,
    readingUuid: station.readingUuid,
    label: station.stationName || "Pluviômetro",
    stationNumber: station.stationNumber || "",
    lat: Number(station.lat),
    lng: Number(station.lng),
    mm: Number(station.mm) || 0,
    periodStart: station.periodStart,
    periodEnd: station.periodEnd,
    recordedAt: station.recordedAt,
  }));
}

export function buildHeatmapBoundsFromGauges(gauges, padding = 0.01) {
  if (!gauges || !gauges.length) return [];
  const lats = gauges.map((gauge) => gauge.lat);
  const lngs = gauges.map((gauge) => gauge.lng);
  const minLat = Math.min(...lats) - padding;
  const maxLat = Math.max(...lats) + padding;
  const minLng = Math.min(...lngs) - padding;
  const maxLng = Math.max(...lngs) + padding;
  return [
    { lat: minLat, lng: minLng },
    { lat: minLat, lng: maxLng },
    { lat: maxLat, lng: maxLng },
    { lat: maxLat, lng: minLng },
  ];
}

export function resolveHeatmapRing(boundaryRing, gauges) {
  if (Array.isArray(boundaryRing) && boundaryRing.length >= 3) {
    return boundaryRing;
  }
  return buildHeatmapBoundsFromGauges(gauges);
}

function ringFromFarm(farm) {
  if (!farm || !farm.boundary_geojson) return [];
  const ring = parseBoundaryGeoJsonRing(farm.boundary_geojson);
  return ring.length >= 3 ? ring : [];
}

export function listSubFarmBoundaries(groupFarms) {
  return (groupFarms || [])
    .filter((farm) => farm && farm.parent)
    .map((farm) => ({
      id: farm.id,
      name: String(farm.nome_da_fazenda || "").trim(),
      ring: ringFromFarm(farm),
    }))
    .filter((item) => item.ring.length >= 3);
}

export function flattenBoundaryRings(rings) {
  return (rings || []).reduce((points, ring) => {
    if (Array.isArray(ring) && ring.length) {
      return points.concat(ring);
    }
    return points;
  }, []);
}

export function resolvePrimaryBoundaryRing(groupFarms, contextFarmId) {
  const farms = groupFarms || [];
  const current = farms.find(
    (farm) => String(farm.id) === String(contextFarmId)
  );
  const currentRing = ringFromFarm(current);
  if (currentRing.length) return currentRing;

  const rafael = findSanRafaelSubFarm(farms);
  const rafaelRing = ringFromFarm(rafael);
  if (rafaelRing.length) return rafaelRing;

  let largestRing = [];
  farms.forEach((farm) => {
    const ring = ringFromFarm(farm);
    if (ring.length > largestRing.length) {
      largestRing = ring;
    }
  });
  return largestRing;
}

export function parseDateInputLocal(value) {
  if (!value) return null;
  const parts = String(value).split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
    return null;
  }
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

export function shiftDateInput(value, days) {
  const date = parseDateInputLocal(value) || new Date();
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
}

export function eachDateInclusive(startStr, endStr) {
  const start = parseDateInputLocal(startStr);
  const end = parseDateInputLocal(endStr);
  if (!start || !end || start > end) return [];
  const dates = [];
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    dates.push(toDateInputValue(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

export function lastTwelveMonths(today = toDateInputValue()) {
  const date = parseDateInputLocal(today) || new Date();
  const months = [];
  for (let offset = 11; offset >= 0; offset -= 1) {
    const first = new Date(date.getFullYear(), date.getMonth() - offset, 1);
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    const start = toDateInputValue(first);
    const lastDay = toDateInputValue(last);
    const end = lastDay > today ? today : lastDay;
    months.push({
      id: `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, "0")}`,
      year: first.getFullYear(),
      month: first.getMonth() + 1,
      start,
      end,
    });
  }
  return months;
}

export function monthAllDays(monthId) {
  const match = String(monthId || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return [];
  const year = Number(match[1]);
  const month = Number(match[2]);
  const start = toDateInputValue(new Date(year, month - 1, 1));
  const lastDay = toDateInputValue(new Date(year, month, 0));
  return eachDateInclusive(start, lastDay);
}

export function monthDaysUntilToday(monthId, today = toDateInputValue()) {
  const match = String(monthId || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return [];
  const year = Number(match[1]);
  const month = Number(match[2]);
  const start = toDateInputValue(new Date(year, month - 1, 1));
  const lastDay = toDateInputValue(new Date(year, month, 0));
  const end = lastDay > today ? today : lastDay;
  if (start > today) return [];
  return eachDateInclusive(start, end);
}

export function buildMonthlyRainSeries(months, gauges, dailyByStation) {
  return (months || []).map((item) => {
    const days = eachDateInclusive(item.start, item.end);
    const daily = buildDailyRainSeries(days, gauges, dailyByStation);
    const mm = daily.reduce((total, row) => total + (Number(row.mm) || 0), 0);
    return { ...item, mm };
  });
}

const EXAMPLE_MONTH_MM = [48, 12, 76, 8, 0, 34, 91, 22, 15, 60, 5, 28];

export function buildExampleMonthlyRainSeries(months) {
  return (months || []).map((item, index) => ({
    ...item,
    mm: EXAMPLE_MONTH_MM[index % EXAMPLE_MONTH_MM.length],
    example: true,
  }));
}

export function rainChartRange(lookbackId, today = toDateInputValue()) {
  const spanById = {
    "30d": 30,
    "3m": 90,
    "6m": 180,
    "12m": 365,
  };
  const span = spanById[lookbackId] || 30;
  return {
    start: shiftDateInput(today, -(span - 1)),
    end: today,
  };
}

export const RAIN_CHART_LOOKBACKS = [
  { id: "30d", label: "Últimos 30 dias" },
  { id: "3m", label: "Últimos 3 meses" },
  { id: "6m", label: "Últimos 6 meses" },
  { id: "12m", label: "Últimos 12 meses" },
];

export function clampRainWindow(start, end, rangeStart, rangeEnd) {
  const lo = rangeStart || start;
  const hi = rangeEnd || end;
  let nextStart = start && start < lo ? lo : start;
  let nextEnd = end && end > hi ? hi : end;
  if (!nextStart) nextStart = lo;
  if (!nextEnd) nextEnd = hi;
  if (nextStart > nextEnd) {
    return { start: lo, end: hi };
  }
  return { start: nextStart, end: nextEnd };
}

export function eachDateOfMonth(dateStr) {
  const date = parseDateInputLocal(dateStr) || new Date();
  const start = toDateInputValue(new Date(date.getFullYear(), date.getMonth(), 1));
  const end = toDateInputValue(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  return eachDateInclusive(start, end);
}

export function shiftMonthInput(dateStr, months) {
  const date = parseDateInputLocal(dateStr) || new Date();
  return toDateInputValue(
    new Date(date.getFullYear(), date.getMonth() + Number(months || 0), 1)
  );
}

export function buildDailyRainSeries(days, gauges, dailyByStation) {
  return (days || []).map((day) => {
    const values = (gauges || []).map((gauge) => {
      const key = gauge.stationUuid || gauge.id;
      const daily = dailyByStation && key ? dailyByStation[key] : null;
      return Number(daily && daily[day]) || 0;
    });
    const mm = values.length
      ? values.reduce((total, value) => total + value, 0) / values.length
      : 0;
    return { date: day, mm };
  });
}

const EXAMPLE_RAIN_MM = [
  0, 0, 18, 6, 0, 0, 32, 14, 0, 4, 0, 0, 22, 8, 0, 0, 0, 41, 19, 3, 0, 0, 12,
  0, 27, 9, 0, 0, 15, 0,
];

export function buildExampleDailyRainSeries(days) {
  return (days || []).map((day, index) => ({
    date: day,
    mm: EXAMPLE_RAIN_MM[index % EXAMPLE_RAIN_MM.length],
    example: true,
  }));
}

export function seriesHasRain(series) {
  return (series || []).some((row) => Number(row.mm) > 0);
}

export function resolveRainScale(period, customStart, customEnd, today = toDateInputValue()) {
  if (period === "custom") {
    return {
      start: customStart || today,
      end: customEnd || today,
    };
  }
  if (period === "week") {
    return { start: shiftDateInput(today, -6), end: today };
  }
  if (period === "month") {
    return { start: shiftDateInput(today, -30), end: today };
  }
  return { start: shiftDateInput(today, -29), end: today };
}

export function resolveRainWindow(period, scaleStart, scaleEnd, today = toDateInputValue()) {
  if (period === "today") {
    return { start: today, end: today };
  }
  return { start: scaleStart, end: scaleEnd };
}

export function yearsForDateRange(startStr, endStr) {
  const start = parseDateInputLocal(startStr);
  const end = parseDateInputLocal(endStr);
  if (!start || !end) return [new Date().getFullYear()];
  const years = [];
  for (let year = start.getFullYear(); year <= end.getFullYear(); year += 1) {
    years.push(year);
  }
  return years;
}

export function flattenSheetCells(sheet) {
  const cells = (sheet && sheet.cells) || {};
  const daily = {};
  Object.keys(cells).forEach((key) => {
    const cell = cells[key];
    const mm =
      cell && typeof cell === "object"
        ? Number(cell.rainMm)
        : Number(cell);
    if (Number.isFinite(mm)) {
      daily[key] = mm;
    }
  });
  return daily;
}

export function findLastRainDate(daily) {
  if (!daily || typeof daily !== "object") return null;
  let last = null;
  Object.keys(daily).forEach((key) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return;
    const mm = Number(daily[key]);
    if (!Number.isFinite(mm) || mm <= 0) return;
    if (!last || key > last) last = key;
  });
  return last;
}

export function rainDaysInRange(daily, startStr, endStr) {
  return eachDateInclusive(startStr, endStr).filter((day) => {
    const mm = Number(daily && daily[day]);
    return Number.isFinite(mm) && mm > 0;
  });
}

export function findLastRainDateInRange(daily, startStr, endStr) {
  const days = rainDaysInRange(daily, startStr, endStr);
  return days.length ? days[days.length - 1] : null;
}

export function sumDailyRange(daily, startStr, endStr) {
  return eachDateInclusive(startStr, endStr).reduce((total, day) => {
    return total + (Number(daily && daily[day]) || 0);
  }, 0);
}

export function applyDailyWindow(gauges, dailyByStation, windowStart, windowEnd) {
  if (!dailyByStation || !Object.keys(dailyByStation).length) {
    return gauges || [];
  }
  return (gauges || []).map((gauge) => {
    const key = gauge.stationUuid || gauge.id;
    if (!dailyByStation[key]) return gauge;
    return {
      ...gauge,
      mm: sumDailyRange(dailyByStation[key], windowStart, windowEnd),
    };
  });
}

export function toDateInputValue(date = new Date()) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function formatDateInputBr(value) {
  if (!value) return "—";
  const parts = String(value).split("-");
  if (parts.length !== 3) return value;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

export function formatRainPeriodLabel(period, customStart, customEnd, translate) {
  const t = translate || ((key) => key);
  if (period === "custom") {
    if (customStart && customEnd && customStart === customEnd) {
      return formatDateInputBr(customStart);
    }
    if (customStart || customEnd) {
      return `${formatDateInputBr(customStart)} – ${formatDateInputBr(customEnd)}`;
    }
    return t("Personalizado");
  }
  const labels = {
    today: t("Hoje"),
    week: t("Última semana"),
    month: t("Último mês"),
  };
  return labels[period] || t("Hoje");
}

export function resolveQuickRainDate(period, customStart, customEnd) {
  if (period === "today") return toDateInputValue();
  if (period === "custom" && customStart && customEnd && customStart === customEnd) {
    return customStart;
  }
  return null;
}

export const DEFAULT_RAIN_MAP_ZOOM = 16;
export const RAIN_MAP_FIT_MAX_SPAN_DEG = 0.25;
export const RAIN_MAP_MIN_ZOOM = 10;
export const RAIN_MAP_MAX_ZOOM = 19;
export const RAIN_MAP_FALLBACK_CENTER = { lat: -15.78, lng: -47.93 };

export function isValidMapCenter(center) {
  if (!center) return false;
  const lat = Number(center.lat);
  const lng = Number(center.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) < 0.0001 && Math.abs(lng) < 0.0001) return false;
  return lat >= -85 && lat <= 85 && lng >= -180 && lng <= 180;
}

export function clampRainMapZoom(zoom, fallback = DEFAULT_RAIN_MAP_ZOOM) {
  const value = Number(zoom);
  if (!Number.isFinite(value)) return fallback;
  return Math.min(RAIN_MAP_MAX_ZOOM, Math.max(RAIN_MAP_MIN_ZOOM, value));
}

export const RAIN_GAUGE_FULL_LABEL_MAX_SCALE_M = 500;
export const RAIN_MAP_SCALE_BAR_PIXELS = 100;
const RAIN_MAP_NICE_SCALE_STEPS = [
  1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000,
  100000,
];

export function rainMapMetersPerPixel(zoom, latitude) {
  const z = Number(zoom);
  const lat = Number(latitude);
  if (!Number.isFinite(z) || !Number.isFinite(lat)) return Infinity;
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, z);
}

export function rainMapScaleMeters({
  zoom,
  latitude,
  barPixels = RAIN_MAP_SCALE_BAR_PIXELS,
} = {}) {
  const metersPerPixel = rainMapMetersPerPixel(zoom, latitude);
  if (!Number.isFinite(metersPerPixel) || metersPerPixel === Infinity) {
    return Infinity;
  }
  const maxMeters =
    metersPerPixel * Number(barPixels || RAIN_MAP_SCALE_BAR_PIXELS);
  let nice = RAIN_MAP_NICE_SCALE_STEPS[0];
  RAIN_MAP_NICE_SCALE_STEPS.forEach((step) => {
    if (step <= maxMeters) nice = step;
  });
  return nice;
}

export function shouldShowRainGaugeFullName(scaleMeters) {
  return Number(scaleMeters) <= RAIN_GAUGE_FULL_LABEL_MAX_SCALE_M;
}

export function rainGaugeCompactId(gauge) {
  if (!gauge) return "";
  const number = String(gauge.stationNumber || "").trim();
  if (number) return number;
  const label = String(gauge.label || "").trim();
  const trailing = label.match(/(\d+)\s*$/);
  if (trailing) return trailing[1];
  const stripped = label.replace(/^pluvi[oô]metro\s*/i, "").trim();
  return stripped || label;
}

export function rainGaugeMapLabel(gauge, showFullName) {
  if (showFullName) {
    return (gauge && gauge.label) || "Pluviômetro";
  }
  return rainGaugeCompactId(gauge);
}

export function isUsableRainViewport(viewport) {
  if (!viewport || !isValidMapCenter(viewport)) return false;
  const zoom = Number(viewport.zoom);
  return Number.isFinite(zoom) && zoom >= RAIN_MAP_MIN_ZOOM && zoom <= RAIN_MAP_MAX_ZOOM;
}

export function shouldUseCloseRainZoom(points) {
  const list = (points || []).filter(
    (point) =>
      point &&
      Number.isFinite(Number(point.lat)) &&
      Number.isFinite(Number(point.lng))
  );
  if (list.length < 2) return true;
  const lats = list.map((point) => Number(point.lat));
  const lngs = list.map((point) => Number(point.lng));
  return (
    Math.max(...lats) - Math.min(...lats) > RAIN_MAP_FIT_MAX_SPAN_DEG ||
    Math.max(...lngs) - Math.min(...lngs) > RAIN_MAP_FIT_MAX_SPAN_DEG
  );
}

export function parseLandPlotRing(plot) {
  if (!plot || !plot.geomap) return [];
  try {
    const geometry = JSON.parse(plot.geomap);
    const coords = geometry && geometry.coordinates && geometry.coordinates[0];
    if (!Array.isArray(coords)) return [];
    return coords
      .map((coord) => ({
        lat: Number(coord[1]),
        lng: Number(coord[0]),
      }))
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  } catch (error) {
    return [];
  }
}

export function rainMmAtRingCentroid(ring, gauges) {
  const centroid = computeRingCentroid(ring);
  if (!centroid || !gauges || !gauges.length) return null;
  return interpolateRainMmAtPoint(centroid, gauges);
}

export function gaugesInsideRing(gauges, ring) {
  if (!Array.isArray(ring) || ring.length < 3) {
    return gauges || [];
  }
  return (gauges || []).filter((gauge) => {
    const lat = Number(gauge && gauge.lat);
    const lng = Number(gauge && gauge.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    return pointInPolygon({ lat, lng }, ring);
  });
}

function ringBbox(ring) {
  const lats = ring.map((point) => Number(point.lat));
  const lngs = ring.map((point) => Number(point.lng));
  return {
    minLat: Math.min(...lats),
    maxLat: Math.max(...lats),
    minLng: Math.min(...lngs),
    maxLng: Math.max(...lngs),
  };
}

function bboxArea(box) {
  return (
    Math.max(0, box.maxLat - box.minLat) * Math.max(0, box.maxLng - box.minLng)
  );
}

function overlapArea(a, b) {
  const minLat = Math.max(a.minLat, b.minLat);
  const maxLat = Math.min(a.maxLat, b.maxLat);
  const minLng = Math.max(a.minLng, b.minLng);
  const maxLng = Math.min(a.maxLng, b.maxLng);
  if (minLat >= maxLat || minLng >= maxLng) return 0;
  return (maxLat - minLat) * (maxLng - minLng);
}

export function resolveSubFarmFromViewport(viewport, subFarms) {
  const farms = (subFarms || []).filter(
    (farm) => farm && Array.isArray(farm.ring) && farm.ring.length >= 3
  );
  if (!viewport || !viewport.center || !farms.length) return null;

  const viewBox = {
    minLat: Number(viewport.south),
    maxLat: Number(viewport.north),
    minLng: Number(viewport.west),
    maxLng: Number(viewport.east),
  };
  const viewArea = bboxArea(viewBox);
  if (!(viewArea > 0)) return null;

  let best = null;
  let bestOverlap = 0;
  farms.forEach((farm) => {
    const overlap = overlapArea(ringBbox(farm.ring), viewBox);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      best = farm;
    }
  });

  if (best && bestOverlap / viewArea >= 0.45) {
    return String(best.id);
  }

  const inside = farms.filter((farm) =>
    pointInPolygon(viewport.center, farm.ring)
  );
  if (inside.length === 1) return String(inside[0].id);
  return null;
}

export function computeRainMapCenter(boundaryRing, gauges) {
  const centroid = computeRingCentroid(boundaryRing);
  if (centroid) {
    return { center: centroid, zoom: DEFAULT_RAIN_MAP_ZOOM };
  }

  if (gauges && gauges.length) {
    const lat =
      gauges.reduce((sum, gauge) => sum + gauge.lat, 0) / gauges.length;
    const lng =
      gauges.reduce((sum, gauge) => sum + gauge.lng, 0) / gauges.length;
    return { center: { lat, lng }, zoom: DEFAULT_RAIN_MAP_ZOOM };
  }

  return { center: null, zoom: DEFAULT_RAIN_MAP_ZOOM };
}
